import { Controller } from "@hotwired/stimulus"

// Tiny helper that fetches a URL and injects the response HTML into a
// target slot. Designed to pair with `modal_controller`: the modal body
// stays empty until the first time the modal opens, then this controller
// fills it in.
//
// Markup (paired with the modal_controller):
//   <div data-controller="lazy-content"
//        data-lazy-content-url-value="/portfolio/risks/candidates"
//        data-action="modal:opened@window->lazy-content#load">
//     <div data-lazy-content-target="slot">
//       <%# placeholder shown until first load %>
//     </div>
//   </div>
//
// The action MUST listen at @window — modal_controller dispatches
// `modal:opened` from the modal element, which bubbles UP. The
// lazy-content element sits INSIDE the modal, so a non-window listener
// never receives the event. The handler filters at window scope by
// checking the event target contains this.element.
//
// The fetch runs once per controller instance (on first `load` call).
// Re-opening the modal does not refetch — set `data-lazy-content-refetch-value="true"`
// to force a fresh fetch every open.
export default class extends Controller {
  static targets = ["slot"]
  static values = {
    url: String,
    refetch: { type: Boolean, default: false },
    loaded: { type: Boolean, default: false }
  }

  load(event) {
    // When listening at window, every modal:opened event lands here.
    // Only react if the event originated from a modal that contains us.
    if (event && event.target && !event.target.contains(this.element)) return
    if (this.loadedValue && !this.refetchValue) return
    this._fetch(this.urlValue)
  }

  // Replace the slot content with the response from the link's href.
  // Useful for in-modal tab links — the link still works as a normal
  // navigation outside the modal because Stimulus actions only fire
  // when the controller is present.
  navigate(event) {
    const link = event.currentTarget
    const href = link.getAttribute("href")
    if (!href) return
    event.preventDefault()
    this.urlValue = href
    this._fetch(href)
  }

  async _fetch(url) {
    if (!url) return
    this._showLoading()
    try {
      const res = await fetch(url, {
        headers: {
          "Accept": "text/html",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const html = await res.text()
      this.slotTarget.innerHTML = html
      this.loadedValue = true
    } catch (err) {
      this.slotTarget.innerHTML =
        `<div class="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">Failed to load content. Refresh and try again.</div>`
      console.error("[lazy-content] fetch failed", err)
    }
  }

  _showLoading() {
    this.slotTarget.innerHTML =
      `<div class="flex items-center justify-center py-10 text-[13px] text-gray-500">Loading…</div>`
  }
}
